import React, { useEffect, useState } from "react";
import { Link } from "react-router";
import Markdown from "react-markdown";
import { formatRelative } from "date-fns";
import { toast } from "react-toastify";
import {
  ArrowRightIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

import { makeRequest } from "../../../lib/request";
import Button from "../../atoms/Button";
import { useUserStore } from "../../../store/userStore";

function GalleryComments({ postId }: { postId: number }) {
  const user = useUserStore((state) => state.user);
  const roles = useUserStore((state) => state.roles);
  const accessToken = useUserStore((state) => state.accessToken);
  const impersonationToken = useUserStore((state) => state.impersonationToken);

  const [comment, setComment] = useState("");
  const [comments, setComments] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getComments();
  }, []);

  const sendComment = async () => {
    if (comment === "" || loading) {
      return;
    }

    setLoading(true);

    const { status, message } = await makeRequest(`private/gallery/comment`, {
      method: "POST",
      body: JSON.stringify({ postId, comment }),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${impersonationToken || accessToken}`,
      },
    });

    if (status) {
      await getComments();
      setComment("");
    } else {
      toast.error(message);
    }

    setLoading(false);
  };

  const deleteComment = async (commentId: number) => {
    if (loading) {
      return;
    }

    const c = window.confirm("Are you sure you want to delete this comment?");

    if (!c) {
      return;
    }

    setLoading(true);

    const { status, message } = await makeRequest(`private/gallery/comment`, {
      method: "DELETE",
      body: JSON.stringify({ commentId }),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${impersonationToken || accessToken}`,
      },
    });

    if (status) {
      await getComments();
      toast.success("Comment deleted.");
    } else {
      toast.error(message);
    }

    setLoading(false);
  };

  const getComments = async () => {
    const { status, message, comments } = await makeRequest(
      `public/gallery/comments?postId=${postId}`,
      {
        cache: "no-cache",
      }
    );

    if (status) {
      setComments(comments);
    } else {
      toast.error(message);
    }
  };

  return (
    <>
      <div>
        {user ? (
          <div>
            <div className="flex flex-row items-center w-full">
              <div className="w-full">
                <input
                  className="w-full !rounded-none !rounded-l-md border border-r-0 bg-surface-raised text-text-base border border-border-faint h-[2.8rem] pl-3"
                  placeholder="Add comment"
                  aria-label="Add comment"
                  type="text"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      sendComment();
                    }
                  }}
                />
              </div>
              <div className="ml-auto">
                <Button
                  style="secondary"
                  aria-label="Post comment"
                  className={`bg-surface-primary p-3 rounded-none rounded-r-lg border-l-0 ${
                    loading ? "opacity-50 cursor-not-allowed" : "opacity-100"
                  }`}
                  onClick={() => {
                    if (loading) {
                      return;
                    }

                    sendComment();
                  }}
                >
                  <ArrowRightIcon className="w-5 h-5 text-text-inverted" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          ""
        )}
        <div className="pt-5">
          {comments ? (
            <div className="flex flex-col gap-3">
              {comments.map((comment: any) => (
                <div
                  key={comment.id}
                  className="text-text-base flex flex-row gap-3 items-start"
                >
                  <div className="min-w-[3rem] w-[3rem] h-[3rem]">
                    <img
                      src={`https://images.givin.gifts/preset:sharp/resize:fit:720:0:0/format:webp/gravity:sm/plain/https://storage.ethereal.sh/gifts-public/${comment.user.picture}`}
                      alt=""
                      className="w-[3rem] h-[3rem] object-cover rounded-lg shadow-sm"
                      width={32}
                      height={32}
                    />
                  </div>
                  <div className="bg-surface-raised rounded-xl w-full shadow-sm flex flex-col gap-2 p-3">
                    <div className="text-sm flex flex-row items-start gap-2">
                      <div>
                        <Link
                          to={`/profile/${comment.user.nickname}`}
                          className="text-text-base underline font-bold underline-offset-2 hover:underline-offset-4"
                        >
                          {comment.user.nickname}
                        </Link>{" "}
                        <span className="ml-1 text-text-base font-normal">
                          {formatRelative(
                            new Date(comment.createdAt),
                            new Date()
                          )}
                        </span>
                      </div>
                      {user?.nickname === comment.user.nickname ||
                      !roles ||
                      roles.includes("admin") ? (
                        <div className="flex items-center ml-auto mr-0">
                          <Button
                            style="tertiary"
                            className={`justify-center flex flex-row gap-2 items-center ml-auto mr-0 rounded-lg shadow-none !p-1`}
                            onClick={() => {
                              deleteComment(comment.id);
                            }}
                          >
                            <TrashIcon className="w-5 h-5 text-rag-error" />
                            <span className="sr-only text-text-base font-semibold">
                              Delete comment
                            </span>
                          </Button>
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                    <div>
                      <Markdown>{comment.comment}</Markdown>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="font-italic flex flex-row gap-3">
              <ChatBubbleOvalLeftEllipsisIcon className="w-6 h-6 text-text-base" />
              This post currently has no comments
            </p>
          )}
        </div>
      </div>
    </>
  );
}

export default GalleryComments;
